import { useState } from "react";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { reportEnquiryList } from "../../../../services/enquiryService";
import LimitSelect from "../../../elements/LimitSelect";
import { handleFilterChange } from "../../../../helpers";
import Search from "../../../form/Search";
import Table from "../../../elements/table/Table";
import ReportModal from "./ReportModal";
import { useOutletContext } from "react-router-dom";

const SpamReport = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    data: null,
  });
  const { event } = useOutletContext();
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt", // Sort by newest first
  });

  // Fetch enquiry data
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["spam-report", filters, search],
    queryFn: async () => {
      const res = await reportEnquiryList({
        ...filters,
        ...(search && { keyword: search }),
        type: "event-report",
        referenceId: event?.data?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  // Table header configuration
  const tableHeadData = [
    {
      _id: 1,
      name: "User Name",
      align: "left",
    },
    {
      _id: 2,
      name: "User Email",
      align: "left",
    },
    {
      _id: "reason",
      name: "reason",
      align: "left",
    },
    {
      _id: 3,
      name: "Message",
      align: "left",
    },
    {
      _id: 4,
      name: "Action",
      align: "left",
    },
  ];

  // Table data mapping
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[200px]",
          data: <div className="!font-semibold">{item?.name}</div>,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[200px]",
          data: <div className="text-sm text-gray-600">{item?.email}</div>,
        },
        {
          _id: "reason",
          type: "text",
          align: "left",
          cellClass: "!min-w-[250px]",
          data: item?.reason,
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[250px]",
          data: <div className="line-clamp-2">{item?.message || "-"}</div>,
        },
        {
          _id: "action",
          type: "action",
          align: "left",
          cellClass: "w-[10%]",
          actionData: [
            {
              _id: "details",
              name: "View Details",
              icon: "fa-regular fa-eye",
              onClick: () => {
                setDetailsModal({ isOpen: true, data: item });
              },
              standout: false,
            },
          ],
        },
      ],
    };
  });

  return (
    <>
      <div className="space-y-4 px-5">
        <div className="flex justify-between items-center btransaction-b btransaction-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
              Customer Reports
            </div>
            <div className="text-sm xl:text-base h-10">
              Easily observed report details: See all information here.
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full md:w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-full md:w-72">
              <Search
                placeholder="Search enquiries..."
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer="!w-48"
          emptyStateTitle="No Enquiries Found"
          emptyStateDescription="No customer enquiries have been submitted yet."
        />
      </div>

      {/* Details Modal */}
      <ReportModal
        isOpen={detailsModal?.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, data: null })}
        data={detailsModal?.data}
      />
    </>
  );
};

export default SpamReport;
